import type { BuildingType, Planet, PlayerRace, TickCheckpointEventOf } from '@kaeliux/shared';
import { Popover } from '../ui/Popover';
import type { PlanetActionOptions } from '../../services/planet.service';
import type { Rect, PopoverBounds } from '../../utils/popoverPlacement';
import { findPlotOccupant, type SurfacePlot } from './surfaceLayout';
import { PlotChooserContent } from './PlotChooserContent';
import { BuiltPlotContent } from './BuiltPlotContent';

export interface BuildingPopoverProps {
    open: boolean;
    /** The tapped plot (DS-28) — which building, if any, occupies it is resolved live below, not decided by the plot itself. */
    slot: SurfacePlot;
    anchorRect: Rect;
    bounds: PopoverBounds;
    planet: Planet;
    race: PlayerRace | undefined;
    scienceLevels: Partial<Record<string, number>>;
    projectedLevels: Partial<Record<BuildingType, number>>;
    pendingBuildsByBuilding: Partial<Record<BuildingType, TickCheckpointEventOf<'buildingBuild'>[]>>;
    /**
     * Pending level 0→1 constructs, keyed by the plot they claimed (DS-28) —
     * only these carry a `plotId` at all. Lets a plot with a construct
     * already queued on it show that construct's progress instead of
     * offering the chooser (and the plot it's not offered again for) even
     * before the building itself exists in `planet.buildings`.
     */
    pendingConstructsByPlot: Partial<Record<string, TickCheckpointEventOf<'buildingBuild'>[]>>;
    queueFull: boolean;
    options: PlanetActionOptions;
    now: number;
    onClose: () => void;
}

/**
 * Popover opened by `PlanetSurface` on a tap (DS-14's 6px drag threshold) of
 * a `[data-tile-id]` plot (DS-15/DS-25/DS-28) — `PlotChooserContent` for an
 * empty plot, `BuiltPlotContent` (wired to `usePlanetBuildActions`, shared
 * with `ZonePage`) for one with a building on it. DS-28 replaced the fixed
 * plot↔building binding with free placement, so which building (if any)
 * occupies the tapped plot is resolved here via `findPlotOccupant` against
 * live `Planet.buildings` data, not read off the plot itself.
 * `projectedLevels`/`pendingBuildsByBuilding` come in as full per-building
 * maps rather than a single value scoped to the occupant, because an empty
 * plot's chooser needs every unbuilt building in the zone, not just one.
 */
export function BuildingPopover({
    open,
    slot,
    anchorRect,
    bounds,
    planet,
    race,
    scienceLevels,
    projectedLevels,
    pendingBuildsByBuilding,
    pendingConstructsByPlot,
    queueFull,
    options,
    now,
    onClose,
}: BuildingPopoverProps) {
    const occupant = findPlotOccupant(planet, slot.id);
    const pendingConstruct = pendingConstructsByPlot[slot.id]?.[0];
    // A plot already claimed by a queued construct shows that construct's
    // progress — the building it's for isn't in `planet.buildings` yet, so
    // `occupant` alone can't see this, and the chooser must not offer this
    // plot again for a second one.
    const constructBuildingType = occupant ?? pendingConstruct?.eventDetails.buildingType;

    return (
        <Popover open={open} anchorRect={anchorRect} bounds={bounds} onDismiss={onClose} className="z-50" data-testid="building-popover">
            {!constructBuildingType ? (
                <PlotChooserContent
                    zone={slot.zone}
                    plotId={slot.id}
                    planet={planet}
                    race={race}
                    scienceLevels={scienceLevels}
                    projectedLevels={projectedLevels}
                    pendingBuildsByBuilding={pendingBuildsByBuilding}
                    queueFull={queueFull}
                    options={options}
                    now={now}
                    onClose={onClose}
                />
            ) : (
                <BuiltPlotContent
                    buildingType={constructBuildingType}
                    zone={slot.zone}
                    plotId={slot.id}
                    planet={planet}
                    race={race}
                    scienceLevels={scienceLevels}
                    projectedLevel={projectedLevels[constructBuildingType] ?? 0}
                    queuedEvents={occupant ? (pendingBuildsByBuilding[occupant] ?? []) : (pendingConstruct ? [pendingConstruct] : [])}
                    queueFull={queueFull}
                    options={options}
                    now={now}
                    onClose={onClose}
                />
            )}
        </Popover>
    );
}

export default BuildingPopover;
